import { useEffect, useRef, useState } from 'react';

import { searchCities } from '../lib/geo.js';

/**
 * Location control: browser geolocation with a manual city-search fallback for when
 * the user declines the prompt (or the browser has no geolocation at all).
 */
export default function LocationBar({
  location,
  status,
  error,
  onRequestBrowser,
  onManualLocation,
  onClear,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Debounced city search.
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const found = await searchCities(query);
        if (!cancelled) {
          setResults(found);
          setOpen(true);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  // Close the dropdown on outside click.
  useEffect(() => {
    function onDocumentClick(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, []);

  function choose(city) {
    onManualLocation(city);
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  const label = location
    ? [location.city, location.state].filter(Boolean).join(', ') ||
      `${location.lat.toFixed(2)}, ${location.long.toFixed(2)}`
    : null;

  return (
    <div className="location-bar" ref={containerRef}>
      {location ? (
        <div className="location-current">
          <span className="location-pin" aria-hidden="true">
            📍
          </span>
          <span className="location-label">
            {label}
            {location.source === 'browser' ? (
              <span className="location-source"> · from your device</span>
            ) : null}
          </span>
          <button type="button" className="link-button" onClick={onClear}>
            Change
          </button>
        </div>
      ) : (
        <div className="location-empty">
          <button
            type="button"
            className="button button-primary"
            onClick={onRequestBrowser}
            disabled={status === 'locating'}
          >
            {status === 'locating' ? 'Locating…' : 'Use my location'}
          </button>
          <span className="location-or">or</span>
          <div className="location-search">
            <input
              type="search"
              className="input"
              placeholder="Search a city…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onFocus={() => results.length > 0 && setOpen(true)}
              aria-label="Search for a city"
            />
            {open && results.length > 0 ? (
              <ul className="location-results" role="listbox">
                {results.map((city) => (
                  <li key={`${city.id}-${city.lat}`}>
                    <button type="button" onClick={() => choose(city)}>
                      {city.label}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {searching ? <span className="location-searching">…</span> : null}
          </div>
        </div>
      )}

      {status === 'denied' && error ? (
        <p className="location-error">
          {error}. Search for a city instead — distance filtering needs a location.
        </p>
      ) : null}
    </div>
  );
}
